import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdSend, MdSave, MdDelete, MdCode, MdSmartToy, MdKey, MdExpandMore, MdExpandLess, MdContentCopy, MdCheck } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import { useAuth } from '../../context/AuthContext';
import PlotlyChart from '../../components/charts/PlotlyChart';

const PROVIDERS = [
  { id: 'gemini', label: 'Gemini', color: 'text-blue-500' },
  { id: 'claude', label: 'Claude', color: 'text-purple-500' },
  { id: 'openai', label: 'OpenAI', color: 'text-green-500' },
];

const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold';


/* --- Structured Message Component — renders JSON sections from LLM --- */
const StructuredMessage = ({ sections, actions, onQuickReply }) => {
  const [codeExpanded, setCodeExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatInline = (text) => {
    if (!text) return text;
    const parts = [];
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let last = 0;
    let m;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      if (m[2]) parts.push(<strong key={m.index} className="font-semibold text-gray-900 dark:text-gray-100">{m[2]}</strong>);
      else if (m[3]) parts.push(<em key={m.index} className="italic">{m[3]}</em>);
      else if (m[4]) parts.push(<code key={m.index} className="px-1 py-0.5 text-xs bg-gray-700 rounded text-brand-gold font-mono">{m[4]}</code>);
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length > 0 ? parts : text;
  };

  return (
    <div className="max-w-[90%] space-y-2">
      {(sections || []).map((sec, i) => {
        switch (sec.type) {
          case 'heading':
            return <h3 key={i} className="text-base font-bold text-gray-900 dark:text-gray-200 mt-2 mb-1">{sec.content}</h3>;
          case 'text':
            // Render markdown-style formatting: headers, lists, line breaks
            return <div key={i} className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed space-y-2">
              {(sec.content || '').split('\n').map((line, li) => {
                const trimmed = line.trim();
                if (!trimmed) return null;
                // ### Header
                if (trimmed.startsWith('### ')) return <h4 key={li} className="text-sm font-bold text-gray-200 mt-3 mb-1">{trimmed.slice(4)}</h4>;
                if (trimmed.startsWith('## ')) return <h3 key={li} className="text-base font-bold text-gray-200 mt-3 mb-1">{trimmed.slice(3)}</h3>;
                // Numbered list: 1. Item
                if (/^\d+\.\s/.test(trimmed)) return <div key={li} className="ml-2 flex gap-2"><span className="text-brand-gold font-bold shrink-0">{trimmed.match(/^\d+/)[0]}.</span><span>{formatInline(trimmed.replace(/^\d+\.\s*/, ''))}</span></div>;
                // Bullet list: - Item
                if (trimmed.startsWith('- ')) return <div key={li} className="ml-4 flex gap-2"><span className="text-gray-500">•</span><span>{formatInline(trimmed.slice(2))}</span></div>;
                // Separator
                if (trimmed === '---') return <hr key={li} className="border-gray-700 my-2" />;
                // Normal paragraph
                return <p key={li}>{formatInline(trimmed)}</p>;
              })}
            </div>;
          case 'bullets':
            return (
              <ul key={i} className="list-disc ml-4 space-y-0.5 text-sm text-gray-300">
                {(sec.items || []).map((li, j) => <li key={j}>{formatInline(li)}</li>)}
              </ul>
            );
          case 'options':
            return (
              <div key={i} className="space-y-1.5 my-2">
                {sec.prompt && <p className="text-xs text-gray-400 mb-1">{sec.prompt}</p>}
                {(sec.items || []).map((opt, j) => (
                  <button key={j} onClick={() => onQuickReply(opt.value || `Let's go with: ${opt.label}`)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-800/30 hover:border-brand-gold/50 hover:bg-brand-gold/5 hover:shadow-sm transition-colors group">
                    <div className="flex items-start gap-2.5">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-gray-700/50 text-xs font-bold flex items-center justify-center text-gray-400 group-hover:bg-brand-gold/20 group-hover:text-brand-gold">{j + 1}</span>
                      <div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">{opt.label}</div>
                        {opt.description && <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            );
          case 'params':
            return (
              <div key={i} className="p-3 rounded-lg border border-gray-700 bg-gray-100 dark:bg-gray-800/30 space-y-2 my-2">
                {sec.prompt && <div className="text-xs font-medium text-gray-400">{sec.prompt}</div>}
                {(sec.items || []).map((p, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm">
                    <span className="text-gray-400 min-w-[120px]">{p.name}:</span>
                    <code className="px-1.5 py-0.5 bg-gray-700 rounded text-brand-gold text-xs">{String(p.default)}</code>
                    {p.description && <span className="text-xs text-gray-500">({p.description})</span>}
                  </div>
                ))}
              </div>
            );
          case 'code': {
            const preview = (sec.content || '').split('\n').slice(0, 3).join('\n');
            return (
              <div key={i} className="rounded-lg border border-gray-700 overflow-hidden bg-gray-900 my-2">
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800 border-b border-gray-700">
                  <div className="flex items-center gap-2">
                    <MdCode size={14} className="text-brand-gold" />
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">{sec.language || 'python'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { navigator.clipboard.writeText(sec.content); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="p-1 text-gray-400 hover:text-white">
                      {copied ? <MdCheck size={14} className="text-green-400" /> : <MdContentCopy size={14} />}
                    </button>
                    <button onClick={() => setCodeExpanded(!codeExpanded)} className="p-1 text-gray-400 hover:text-white">
                      {codeExpanded ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                    </button>
                  </div>
                </div>
                <pre className={`px-3 py-2 text-xs text-green-400 font-mono overflow-x-auto scrollbar-thin ${codeExpanded ? 'max-h-96' : 'max-h-20'} transition-all`}>
                  {codeExpanded ? sec.content : preview + ((sec.content || '').split('\n').length > 3 ? '\n...' : '')}
                </pre>
              </div>
            );
          }
          default:
            return <p key={i} className="text-sm text-gray-300">{JSON.stringify(sec)}</p>;
        }
      })}

      {/* Action buttons */}
      {actions && actions.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {actions.map((act, i) => (
            <button key={i} onClick={() => onQuickReply(act.value || act.label)}
              className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors ${
                act.primary
                  ? 'bg-brand-gold text-white hover:opacity-90'
                  : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 hover:border-gray-600'
              }`}
            >{act.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};


/* --- Shared helpers --- */
const fmtPct = (v) => v != null ? `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%` : 'N/A';
const fmtNum = (v) => v != null ? Number(v).toFixed(2) : 'N/A';
const valColor = (v) => v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-gray-400';

/* --- Optimize Result Card --- */
const OptimizationConfigCard = ({ data, onRunOptimization }) => {
  const [params, setParams] = useState(() =>
    (data.parameters || []).reduce((acc, p) => ({ ...acc, [p.name]: [...p.range] }), {})
  );
  const [riskEnabled, setRiskEnabled] = useState({});
  const [riskRanges, setRiskRanges] = useState(() =>
    (data.risk_options || []).reduce((acc, r) => ({ ...acc, [r.type]: [...r.default_range] }), {})
  );
  const [maximize, setMaximize] = useState('Sharpe Ratio');
  const [oosSplitPct, setOosSplitPct] = useState(data.oos_split_pct || 0);
  const [addingTo, setAddingTo] = useState(null); // param name being added to
  const [addValue, setAddValue] = useState('');
  const [running, setRunning] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  // Live combo counter
  const totalCombos = useMemo(() => {
    let total = 1;
    Object.values(params).forEach(range => { if (range.length > 0) total *= range.length; });
    Object.entries(riskEnabled).forEach(([type, enabled]) => {
      if (enabled && riskRanges[type]?.length > 0) total *= riskRanges[type].length;
    });
    return total;
  }, [params, riskEnabled, riskRanges]);

  const estTime = Math.max(1, Math.round(totalCombos * 0.15)); // ~150ms per combo

  const addChip = (paramName) => {
    const val = parseFloat(addValue);
    if (!isNaN(val) && !params[paramName]?.includes(val)) {
      setParams(prev => ({ ...prev, [paramName]: [...(prev[paramName] || []), val].sort((a, b) => a - b) }));
    }
    setAddValue('');
    setAddingTo(null);
  };

  const removeRiskChip = (type, value) => {
    setRiskRanges(prev => ({ ...prev, [type]: prev[type].filter(v => v !== value) }));
  };

  const handleRun = async () => {
    setRunning(true);
    const riskConfig = Object.entries(riskEnabled)
      .filter(([, enabled]) => enabled)
      .map(([type]) => {
        const opt = data.risk_options.find(r => r.type === type);
        return { type, [opt?.param || 'pct']: riskRanges[type] };
      });

    try {
      const res = await tradingService.runBacktest({
        mode: 'optimize',
        strategy: data.strategy,
        symbol: data.symbol,
        start_date: data.start_date,
        param_ranges: params,
        maximize,
        risk_config: riskConfig,
        idle_deployment: data.idle_deployment || 'cash',
        oos_split_pct: oosSplitPct,
      });
      if (res.data?.optimization_id && onRunOptimization) {
        onRunOptimization(res.data.optimization_id, res.data.total_combinations);
      }
    } catch (e) {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-full rounded-lg border border-yellow-300 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-900/10 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-yellow-200 dark:border-yellow-800">
        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
          Optimize: {data.strategy_name} on {data.symbol}
          {data.idle_deployment && data.idle_deployment !== 'cash' && (
            <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
              · idle: {data.idle_deployment}
            </span>
          )}
        </h4>
        <div className="text-xs text-gray-500 mt-0.5">
          Customize parameters below, then run
          {data.idle_deployment && data.idle_deployment !== 'cash' && (
            <span> · results include blended metrics (trades + {data.idle_deployment} idle)</span>
          )}
        </div>
      </div>

      {/* Strategy Description + Entry/Exit Rules */}
      <div className="px-4 py-3 border-b border-yellow-200 dark:border-yellow-800">
        <button onClick={() => setDescExpanded(!descExpanded)}
          className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 font-medium hover:text-brand-gold">
          Strategy Details {descExpanded ? '▾' : '▸'}
        </button>
        {descExpanded && (
          <div className="mt-3 space-y-3">
            {data.description && (
              <div className="text-xs text-gray-400 leading-relaxed whitespace-pre-line max-h-40 overflow-y-auto scrollbar-thin">
                {data.description}
              </div>
            )}
            {data.entry_rules?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-green-500 font-medium mb-1">Entry Rules (BUY when ALL true)</div>
                <ul className="text-xs text-gray-400 space-y-0.5 list-disc list-inside">
                  {data.entry_rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {data.exit_rules?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-red-500 font-medium mb-1">Exit Rules (SELL when ANY true)</div>
                <ul className="text-xs text-gray-400 space-y-0.5 list-disc list-inside">
                  {data.exit_rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Strategy Parameters */}
      <div className="px-4 py-3 border-b border-yellow-200 dark:border-yellow-800">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-medium">Strategy Parameters</div>
        <div className="space-y-2">
          {(data.parameters || []).map(p => {
            const isBool = p.type === 'bool';
            const isNullable = !!p.nullable;
            // Display helper: show None as "OFF", booleans as ON / OFF
            const labelOf = (v) => {
              if (v === null || v === undefined) return 'OFF';
              if (typeof v === 'boolean') return v ? 'ON' : 'OFF';
              return String(v);
            };
            const eqVal = (a, b) => (a === null && b === null) || a === b;
            const removeChipTyped = (val) => {
              setParams(prev => ({ ...prev, [p.name]: prev[p.name].filter(v => !eqVal(v, val)) }));
            };
            const hasVal = (val) => (params[p.name] || []).some(v => eqVal(v, val));
            // For booleans, toggle the OTHER value into the range
            const addBool = (val) => {
              if (!hasVal(val)) {
                setParams(prev => ({
                  ...prev,
                  [p.name]: [...(prev[p.name] || []), val],
                }));
              }
            };
            // For nullables, add None (disable) into the range
            const addNone = () => {
              if (!hasVal(null)) {
                setParams(prev => ({
                  ...prev,
                  [p.name]: [null, ...(prev[p.name] || [])],
                }));
              }
            };
            return (
              <div key={p.name} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400 w-32 shrink-0 font-mono" title={p.description || p.name}>
                  {p.name}
                  {p.description && <span className="block text-[9px] text-gray-600 font-sans">{p.description}</span>}
                </span>
                <div className="flex gap-1 flex-wrap">
                  {(params[p.name] || []).map((v, idx) => {
                    const display = labelOf(v);
                    const isOff = (v === null || v === false);
                    return (
                      <button key={`${idx}-${display}`} onClick={() => removeChipTyped(v)}
                        className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                          isOff
                            ? 'bg-gray-500/20 text-gray-400 hover:bg-red-500/20 hover:text-red-400'
                            : 'bg-brand-gold/20 text-brand-gold hover:bg-red-500/20 hover:text-red-400'
                        }`}>
                        {display} ×
                      </button>
                    );
                  })}
                  {/* Add controls — type-aware */}
                  {isBool ? (
                    <>
                      {!hasVal(true) && (
                        <button onClick={() => addBool(true)}
                          className="px-2 py-0.5 text-xs rounded-full border border-dashed border-gray-600 text-gray-500 hover:text-green-400 hover:border-green-400">
                          + ON
                        </button>
                      )}
                      {!hasVal(false) && (
                        <button onClick={() => addBool(false)}
                          className="px-2 py-0.5 text-xs rounded-full border border-dashed border-gray-600 text-gray-500 hover:text-gray-300 hover:border-gray-400">
                          + OFF
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {addingTo === p.name ? (
                        <span className="flex items-center gap-1">
                          <input type="number" step={isNullable ? '0.01' : '1'} value={addValue} onChange={e => setAddValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addChip(p.name)}
                            className="w-16 px-1.5 py-0.5 text-xs bg-gray-800 border border-gray-600 rounded"
                            autoFocus />
                          <button onClick={() => addChip(p.name)} className="text-xs text-green-400">✓</button>
                          <button onClick={() => setAddingTo(null)} className="text-xs text-gray-500">✗</button>
                        </span>
                      ) : (
                        <button onClick={() => setAddingTo(p.name)}
                          className="px-2 py-0.5 text-xs rounded-full border border-dashed border-gray-600 text-gray-500 hover:text-brand-gold hover:border-brand-gold">
                          + Add{isNullable ? ' value' : ''}
                        </button>
                      )}
                      {isNullable && !hasVal(null) && (
                        <button onClick={addNone}
                          className="px-2 py-0.5 text-xs rounded-full border border-dashed border-gray-600 text-gray-500 hover:text-gray-300 hover:border-gray-400">
                          + OFF
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk Modifiers — hidden for strategies with built-in price exits.
          trend_dip_buyer has take_profit_pct / stop_loss_pct / trailing_stop_pct
          as native params (Phase 2) — using both the legacy overlay AND the
          strategy-native version is redundant and confusing. */}
      {data.strategy !== 'trend_dip_buyer' && (
        <div className="px-4 py-3 border-b border-yellow-200 dark:border-yellow-800">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-medium">Risk Modifiers (optional)</div>
          <div className="space-y-2">
            {(data.risk_options || []).map(r => (
              <div key={r.type} className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-1.5 w-32 shrink-0">
                  <input type="checkbox" checked={!!riskEnabled[r.type]}
                    onChange={e => setRiskEnabled(prev => ({ ...prev, [r.type]: e.target.checked }))}
                    className="accent-brand-gold" />
                  <span className="text-xs text-gray-300">{r.label}</span>
                </label>
                {riskEnabled[r.type] && (
                  <div className="flex gap-1 flex-wrap">
                    {(riskRanges[r.type] || []).map(v => (
                      <button key={v} onClick={() => removeRiskChip(r.type, v)}
                        className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400 hover:bg-red-500/20 hover:text-red-400">
                        {v} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer: Maximize + OOS + combo counter + Run */}
      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500">Maximize</label>
            <select value={maximize} onChange={e => setMaximize(e.target.value)}
              className="ml-2 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200">
              {(data.maximize_options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          {/* OOS hold-out selector */}
          <div className="flex items-center gap-1">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 whitespace-nowrap">OOS hold-out</label>
            <div className="flex gap-0.5 ml-1">
              {[0, 10, 20, 30].map(pct => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setOosSplitPct(pct)}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    oosSplitPct === pct
                      ? 'bg-purple-600 border-purple-500 text-white font-semibold'
                      : 'border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-400'
                  }`}
                >
                  {pct === 0 ? 'Off' : `${pct}%`}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-gray-400">
            <span className="font-semibold text-gray-200 tabular-nums">{totalCombos.toLocaleString()}</span> combinations
            <span className="text-gray-600 ml-1">· ~{estTime < 60 ? `${estTime}s` : `${Math.round(estTime / 60)} min`}</span>
          </div>
        </div>
        <button onClick={handleRun} disabled={running || totalCombos === 0}
          className="px-6 py-2 bg-brand-gold text-gray-900 font-semibold text-sm rounded-full hover:bg-brand-gold/80 disabled:opacity-40 transition-colors">
          {running ? 'Starting...' : 'Run Optimization'}
        </button>
      </div>
    </div>
  );
};


const OptimizeResultCard = ({ data }) => {
  if (!data?.success) return <div className="text-xs text-red-400 p-3">{data?.error || 'Optimization failed'}</div>;
  const top = data.top_10 || data.all_results?.slice(0, 10) || [];
  // Detect blended metrics — present when idle_deployment was set on the run.
  // The runner adds blended_return / blended_sharpe / blended_max_drawdown /
  // idle_deployment to each result row when a non-cash idle was provided.
  const idle = data.best?.idle_deployment || top.find(r => r?.idle_deployment)?.idle_deployment;
  const hasBlended = !!idle && idle !== 'cash' && top.some(r => r?.blended_sharpe != null);
  return (
    <div className="max-w-full rounded-lg border border-purple-700/30 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800">
        <h4 className="text-sm font-bold text-gray-200">
          Optimization Results — {data.symbol}
          {hasBlended && <span className="ml-2 text-xs font-normal text-blue-400">· {idle} idle blend</span>}
        </h4>
        <div className="text-[10px] text-gray-600 dark:text-gray-500">{data.combinations_tested || top.length} combinations · maximize: {data.maximize || 'Sharpe'}</div>
      </div>
      {data.best && (
        <div className="px-4 py-2 bg-brand-gold/5 border-b border-gray-800 text-xs">
          <span className="text-brand-gold font-semibold">Best: </span>
          {Object.entries(data.best.parameters || {}).map(([k,v]) => <span key={k} className="mr-2 text-gray-300">{k}={String(v)}</span>)}
          <span className="ml-2 text-green-400">Sharpe {fmtNum(data.best.sharpe_ratio)}</span>
          {hasBlended && (
            <span className="ml-2 text-blue-400">
              · w/ {idle}: Sharpe {fmtNum(data.best.blended_sharpe)} / Return {fmtPct(data.best.blended_return)}
            </span>
          )}
        </div>
      )}
      <div className="overflow-x-auto scrollbar-thin max-h-60">
        <table className="w-full text-[11px]">
          <thead className="bg-gray-100 dark:bg-gray-800/50 text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-2 py-1 text-left">#</th>
              <th className="px-2 py-1 text-left">Params</th>
              <th className="px-2 py-1 text-right">Return</th>
              <th className="px-2 py-1 text-right">Sharpe</th>
              <th className="px-2 py-1 text-right">MaxDD</th>
              <th className="px-2 py-1 text-right">Trades</th>
              {hasBlended && <th className="px-2 py-1 text-right text-blue-400" title={`Blended return: ${data.symbol} trades + ${idle} idle`}>Blend Ret</th>}
              {hasBlended && <th className="px-2 py-1 text-right text-blue-400" title={`Blended Sharpe (with ${idle} idle)`}>Blend Sh</th>}
              {hasBlended && <th className="px-2 py-1 text-right text-blue-400" title={`Blended max drawdown (with ${idle} idle)`}>Blend DD</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {top.map((r, i) => (
              <tr key={i}>
                <td className="px-2 py-1 text-gray-500">{i+1}</td>
                <td className="px-2 py-1 text-gray-300 text-[10px]">{Object.entries(r.parameters||{}).map(([k,v])=>`${k}=${v}`).join(', ')}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${valColor(r.total_return)}`}>{fmtPct(r.total_return)}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${valColor(r.sharpe_ratio)}`}>{fmtNum(r.sharpe_ratio)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-red-400">{fmtPct(r.max_drawdown)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-400">{r.num_trades}</td>
                {hasBlended && <td className={`px-2 py-1 text-right tabular-nums ${valColor(r.blended_return)}`}>{fmtPct(r.blended_return)}</td>}
                {hasBlended && <td className={`px-2 py-1 text-right tabular-nums ${valColor(r.blended_sharpe)}`}>{fmtNum(r.blended_sharpe)}</td>}
                {hasBlended && <td className="px-2 py-1 text-right tabular-nums text-red-400">{fmtPct(r.blended_max_drawdown)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* --- Data Query Result Card — renders table from query_data tool --- */
const DataQueryCard = ({ data }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = data.data || [];
  const cols = data.columns || (rows.length > 0 ? Object.keys(rows[0]) : []);
  const display = showAll ? rows : rows.slice(0, 20);

  return (
    <div className="max-w-full rounded-lg border border-cyan-300 dark:border-cyan-800 bg-cyan-50/30 dark:bg-cyan-900/10 overflow-hidden">
      <div className="px-4 py-2 border-b border-cyan-200 dark:border-cyan-800">
        <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300">
          Query: {data.table} — {data.row_count} row{data.row_count !== 1 ? 's' : ''}{data.total_count && data.total_count > data.row_count ? ` of ${data.total_count}` : ''}
        </h4>
      </div>
      {data.aggregation && data.data?.[0]?.value != null && (
        <div className="px-4 py-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
          {data.aggregation}({data.agg_field}){data.group_by ? ` by ${data.group_by}` : ''} = {typeof data.data[0].value === 'number' ? data.data[0].value.toLocaleString() : data.data[0].value}
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto scrollbar-thin max-h-72">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-100 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500 sticky top-0">
              <tr>{cols.map(c => <th key={c} className="px-2 py-1.5 text-left whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {display.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  {cols.map(c => {
                    const v = r[c];
                    const isNum = typeof v === 'number';
                    return <td key={c} className={`px-2 py-1 tabular-nums whitespace-nowrap ${isNum ? 'text-right' : ''} text-gray-700 dark:text-gray-300`}>
                      {isNum ? v.toLocaleString(undefined, {maximumFractionDigits: 4}) : (v ?? '')}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 20 && (
        <div className="px-4 py-1.5 border-t border-cyan-200 dark:border-cyan-800">
          <button onClick={() => setShowAll(!showAll)} className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline">
            {showAll ? 'Show first 20' : `Show all ${rows.length} rows`}
          </button>
        </div>
      )}
    </div>
  );
};

/* --- Strategy Preview Card — shows logic summary without code --- */
const StrategyPreviewCard = ({ data, onAction }) => {
  const logic = data.logic_summary || {};
  const test = data.test_result || {};
  return (
    <div className="max-w-full rounded-lg border border-purple-300 dark:border-purple-800 bg-purple-50/30 dark:bg-purple-900/10 overflow-hidden">
      <div className="px-4 py-3 border-b border-purple-200 dark:border-purple-800 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">{logic.name || 'Custom Strategy'}</h4>
          <div className="text-xs text-gray-500 mt-0.5">{logic.description || ''}</div>
        </div>
        {data.strategy_key && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">SAVED</span>}
      </div>

      {/* Parameters */}
      {logic.parameters && Object.keys(logic.parameters).length > 0 && (
        <div className="px-4 py-2 border-b border-purple-200 dark:border-purple-800">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Parameters</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(logic.parameters).map(([k, v]) => (
              <span key={k} className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                <span className="text-gray-500">{k}:</span> <strong>{String(v)}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Test Results */}
      <div className="px-4 py-2 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Signals</div>
          <div className="text-sm font-semibold text-gray-300">{(test.buys || 0) + (test.sells || 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Buys</div>
          <div className="text-sm font-semibold text-green-400">{test.buys || 0}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Sells</div>
          <div className="text-sm font-semibold text-red-400">{test.sells || 0}</div>
        </div>
      </div>

      {data.sample_test && (
        <div className="px-4 py-2 border-t border-purple-200 dark:border-purple-800 text-xs text-gray-400">
          {data.sample_test}
        </div>
      )}

      {data.strategy_key && (
        <div className="px-4 py-2 border-t border-purple-200 dark:border-purple-800 text-xs text-gray-500">
          Saved as <code className="text-brand-gold">{data.strategy_key}</code> — ready for backtesting, optimization, and scanning.
        </div>
      )}
    </div>
  );
};

/* --- Compare Result Card --- */
const CompareResultCard = ({ data }) => {
  if (!data?.success) return <div className="text-xs text-red-400 p-3">{data?.error || 'Comparison failed'}</div>;
  const comparisons = data.comparisons || [];
  return (
    <div className="max-w-full rounded-lg border border-blue-700/30 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800">
        <h4 className="text-sm font-bold text-gray-200">Strategy Comparison — {data.symbol}</h4>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 dark:bg-gray-800/50 text-gray-500 uppercase tracking-wider">
            <tr><th className="px-3 py-2 text-left">Strategy</th><th className="px-3 py-2 text-right">Return</th><th className="px-3 py-2 text-right">Sharpe</th><th className="px-3 py-2 text-right">MaxDD</th><th className="px-3 py-2 text-right">Win Rate</th><th className="px-3 py-2 text-right">Trades</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {comparisons.map((c, i) => (
              <tr key={i} className={i === 0 ? 'bg-brand-gold/5' : ''}>
                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{c.strategy_label || c.strategy}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-medium ${valColor(c.total_return)}`}>{c.success ? fmtPct(c.total_return) : 'Error'}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${valColor(c.sharpe_ratio)}`}>{c.success ? fmtNum(c.sharpe_ratio) : '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-red-400">{c.success ? fmtPct(c.max_drawdown) : '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.success ? fmtPct(c.win_rate) : '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-400">{c.success ? c.num_trades : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Equity curves overlay */}
      {comparisons.some(c => c.equity_curve?.length > 0) && (
        <div className="px-4 py-3">
          <PlotlyChart
            data={comparisons.filter(c => c.equity_curve?.length).map((c, i) => ({
              x: c.equity_curve.map(p => p.date), y: c.equity_curve.map(p => p.equity),
              type: 'scatter', mode: 'lines', name: c.strategy_label || c.strategy,
              line: { width: 1.5, color: ['#c9a96e', '#3b82f6', '#ef4444', '#22c55e'][i % 4] },
            }))}
            layout={{ height: 200, margin: { l: 50, r: 10, t: 10, b: 30 }, yaxis: { tickprefix: '$', tickformat: ',.0f', gridcolor: '#1e2530' }, xaxis: { gridcolor: '#1e2530' }, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent', font: { color: '#9ca3af', size: 10 }, showlegend: true, legend: { x: 0, y: 1, font: { size: 9, color: '#9ca3af' } } }}
            style={{ minHeight: 200 }}
          />
        </div>
      )}
    </div>
  );
};

/* --- Price Chart Card --- */
const PriceChartCard = ({ data }) => {
  if (!data?.success) return <div className="text-xs text-red-400 p-3">{data?.error || 'Chart failed'}</div>;
  const prices = data.prices || [];
  if (!prices.length) return <div className="text-xs text-gray-400 p-3">No price data</div>;
  return (
    <div className="max-w-full rounded-lg border border-gray-700 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800">
        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">{data.symbol} — {data.name}</h4>
        <div className="text-[10px] text-gray-600 dark:text-gray-500">{prices.length} bars · {prices[0]?.date} → {prices[prices.length-1]?.date}</div>
      </div>
      <div className="px-4 py-3">
        <PlotlyChart
          data={[{
            x: prices.map(p => p.date), open: prices.map(p => p.open), high: prices.map(p => p.high),
            low: prices.map(p => p.low), close: prices.map(p => p.close),
            type: 'candlestick', increasing: { line: { color: '#22c55e' } }, decreasing: { line: { color: '#ef4444' } },
          }]}
          layout={{ height: 280, margin: { l: 50, r: 10, t: 10, b: 30 }, yaxis: { tickprefix: '$', gridcolor: '#1e2530' }, xaxis: { gridcolor: '#1e2530', rangeslider: { visible: false } }, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent', font: { color: '#9ca3af', size: 10 } }}
          style={{ minHeight: 280 }}
        />
      </div>
    </div>
  );
};

/* --- Data Check Card --- */
const DataCheckCard = ({ data }) => {
  if (!data?.success) return <div className="text-xs text-red-400 p-3">{data?.error || 'Check failed'}</div>;
  const color = data.fill_rate >= 98 ? 'green' : data.fill_rate >= 90 ? 'yellow' : data.fill_rate >= 70 ? 'orange' : 'red';
  return (
    <div className={`max-w-[90%] rounded-lg border border-${color}-700/30 bg-white dark:bg-gray-900/50 p-4`}>
      <h4 className="text-sm font-bold text-gray-200 mb-2">{data.symbol} — Data Availability</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><span className="text-gray-500">Range</span><div className="text-gray-800 dark:text-gray-200">{data.range?.start} → {data.range?.end}</div></div>
        <div><span className="text-gray-500">Expected Days</span><div className="text-gray-800 dark:text-gray-200">{data.expected_days}</div></div>
        <div><span className="text-gray-500">Fill Rate</span><div className={`font-bold text-${color}-400`}>{data.fill_rate}%</div></div>
        <div><span className="text-gray-500">Total Rows</span><div className="text-gray-800 dark:text-gray-200">{data.total_rows}</div></div>
      </div>
      {data.by_source && Object.keys(data.by_source).length > 0 && (
        <div className="flex gap-2 mt-2">
          {Object.entries(data.by_source).map(([src, cnt]) => (
            <span key={src} className="px-2 py-0.5 text-[10px] rounded bg-gray-800 text-gray-400">{src}: {cnt}</span>
          ))}
        </div>
      )}
    </div>
  );
};

/* --- Backtest Result Card — rendered inline in chat with real data --- */
const BacktestResultCard = ({ data }) => {
  const [showTrades, setShowTrades] = useState(false);
  if (!data) return null;

  if (!data.success) {
    return (
      <div className="max-w-[90%] rounded-lg border border-red-700 bg-red-900/20 px-4 py-3">
        <div className="text-sm font-semibold text-red-400 mb-1">Backtest Failed</div>
        <div className="text-xs text-red-300">{data.error || 'Unknown error'}</div>
      </div>
    );
  }

  // Metrics can be at top level OR nested in data.metrics
  const m = data.metrics || data;

  const hasIdle = m.blended_return != null;
  const idleName = m.idle_deployment || 'SPY';
  const metrics = [
    ...(hasIdle ? [
      { label: 'Blended Return', value: fmtPct(m.blended_return), color: valColor(m.blended_return) },
      { label: `${idleName} B&H`, value: fmtPct(m.buy_hold_return), color: valColor(m.buy_hold_return) },
      { label: `Alpha vs ${idleName}`, value: m.alpha != null ? fmtPct(m.alpha) : 'N/A', color: valColor(m.alpha) },
      { label: 'Trades-Only', value: fmtPct(m.trade_only_return || m.total_return), color: valColor(m.trade_only_return || m.total_return) },
    ] : [
      { label: 'Strategy Return', value: fmtPct(m.total_return), color: valColor(m.total_return) },
      { label: 'Buy & Hold', value: fmtPct(m.buy_hold_return), color: valColor(m.buy_hold_return) },
      { label: 'Alpha', value: m.alpha != null ? fmtPct(m.alpha) : 'N/A', color: valColor(m.alpha) },
    ]),
    ...(!hasIdle ? [
      { label: "Jensen's α", value: m.jensens_alpha != null ? fmtPct(m.jensens_alpha) : 'N/A', color: valColor(m.jensens_alpha) },
      { label: 'Beta', value: m.beta != null ? fmtNum(m.beta) : 'N/A', color: 'text-gray-300' },
    ] : []),
    { label: 'Sharpe', value: fmtNum(m.sharpe_ratio), color: valColor(m.sharpe_ratio) },
    { label: hasIdle ? `${idleName} Sharpe` : 'B&H Sharpe', value: m.buy_hold_sharpe != null ? fmtNum(m.buy_hold_sharpe) : 'N/A', color: valColor(m.buy_hold_sharpe) },
    { label: 'Max Drawdown', value: fmtPct(m.max_drawdown), color: 'text-red-400' },
    ...(hasIdle ? [
      { label: `${idleName} Max DD`, value: m.buy_hold_max_dd != null ? fmtPct(m.buy_hold_max_dd) : 'N/A', color: 'text-red-400' },
    ] : []),
    { label: 'Win Rate', value: fmtPct(m.win_rate), color: valColor((m.win_rate || 0) - 50) },
    { label: 'Trades', value: m.num_trades || 0, color: 'text-gray-300' },
    { label: 'Profit Factor', value: fmtNum(m.profit_factor), color: valColor((m.profit_factor || 0) - 1) },
    { label: 'Ann. Return', value: fmtPct(m.annual_return), color: valColor(m.annual_return) },
  ];

  // Equity curve for chart
  const equityCurve = data.equity_curve || [];

  return (
    <div className="max-w-full rounded-lg border border-brand-gold/30 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-200">Backtest Results — {data.symbol}</h4>
          <div className="text-[10px] text-gray-600 dark:text-gray-500">
            {data.strategy} · {data.data_bars} bars
            {m.idle_deployment && m.idle_deployment !== 'cash' && <span className="ml-2 text-blue-400">idle: {m.idle_deployment}</span>}
          </div>
        </div>
        <div className="text-right">
          {hasIdle ? (
            <>
              <span className={`text-lg font-bold tabular-nums ${valColor(m.blended_return)}`}>{fmtPct(m.blended_return)}</span>
              <div className="text-[10px] text-gray-500">blended</div>
            </>
          ) : (
            <span className={`text-lg font-bold tabular-nums ${valColor(m.total_return)}`}>{fmtPct(m.total_return)}</span>
          )}
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-gray-800">
        {metrics.map((m, i) => (
          <div key={i} className="bg-gray-900 px-3 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{m.label}</div>
            <div className={`text-sm font-semibold tabular-nums ${m.color}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Price chart with buy/sell markers */}
      {(data.price_series?.length > 0 || equityCurve.length > 0) && (
        <div className="px-4 py-3">
          <PlotlyChart
            data={[
              // Price line (or equity if no price series)
              {
                x: (data.price_series || equityCurve).map(p => p.date),
                y: (data.price_series || equityCurve).map(p => p.close || p.equity),
                type: 'scatter', mode: 'lines',
                line: { color: '#6b7280', width: 1.5 },
                hovertemplate: `%{x|%b %d %Y}<br>$%{y:,.2f}<extra></extra>`,
                name: 'Price',
              },
              // Buy markers at entry prices
              ...((data.trades || []).length > 0 ? [{
                x: (data.trades || []).map(t => t.entry_date).filter(Boolean),
                y: (data.trades || []).map(t => t.entry_price).filter(Boolean),
                type: 'scatter', mode: 'markers',
                marker: { symbol: 'triangle-up', size: 9, color: '#22c55e', line: { width: 1, color: '#166534' } },
                hovertemplate: 'BUY %{x|%b %d %Y}<br>$%{y:,.2f}<extra></extra>',
                name: 'Buy', showlegend: false,
              }] : []),
              // Sell markers at exit prices
              ...((data.trades || []).filter(t => t.exit_date).length > 0 ? [{
                x: (data.trades || []).map(t => t.exit_date).filter(Boolean),
                y: (data.trades || []).map(t => t.exit_price).filter(v => v),
                type: 'scatter', mode: 'markers',
                marker: { symbol: 'triangle-down', size: 9, color: '#ef4444', line: { width: 1, color: '#991b1b' } },
                hovertemplate: 'SELL %{x|%b %d %Y}<br>$%{y:,.2f}<extra></extra>',
                name: 'Sell', showlegend: false,
              }] : []),
            ]}
            layout={{
              height: 250, margin: { l: 50, r: 10, t: 10, b: 30 },
              yaxis: { tickprefix: '$', tickformat: ',.2f', gridcolor: '#1e2530' },
              xaxis: { gridcolor: '#1e2530' },
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              showlegend: false,
            }}
            style={{ minHeight: 250 }}
          />
        </div>
      )}

      {/* Indicator subplot */}
      {data.indicators && Object.keys(data.indicators).length > 0 && (() => {
        const ind = data.indicators;
        const keys = Object.keys(ind).filter(k => !/ema|sma|ma_|band|upper|lower/i.test(k));
        const colors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6'];
        if (keys.length === 0) return null;
        return (
          <div className="px-4 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Indicators</div>
            <PlotlyChart
              data={keys.map((k, i) => ({
                x: ind[k].map(p => p.date), y: ind[k].map(p => p.value),
                type: 'scatter', mode: 'lines', name: k.replace(/_/g, ' '),
                line: { color: colors[i % colors.length], width: 1 },
              }))}
              layout={{
                height: 120, margin: { l: 40, r: 10, t: 5, b: 20 },
                yaxis: { gridcolor: '#1e2530', tickfont: { size: 9 } },
                xaxis: { gridcolor: '#1e2530', tickfont: { size: 9 } },
                paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                font: { color: '#9ca3af', size: 9 }, showlegend: true,
                legend: { x: 0, y: 1, font: { size: 8, color: '#9ca3af' } },
              }}
              style={{ minHeight: 120 }}
            />
          </div>
        );
      })()}

      {/* Trades toggle */}
      {data.trades && data.trades.length > 0 && (
        <div className="border-t border-gray-800">
          <button onClick={() => setShowTrades(!showTrades)}
            className="w-full px-4 py-2 text-xs text-gray-400 hover:text-gray-200 flex items-center justify-between">
            <span>{data.trades.filter(t => !t.is_idle).length} trades{data.trades.some(t => t.is_idle) ? ` + ${data.trades.filter(t => t.is_idle).length} idle` : ''}</span>
            {showTrades ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
          </button>
          {showTrades && (
            <div className="overflow-x-auto scrollbar-thin max-h-48">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-100 dark:bg-gray-800/50 text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1 text-left">Entry</th>
                    <th className="px-2 py-1 text-left">Exit</th>
                    <th className="px-2 py-1 text-right">Entry $</th>
                    <th className="px-2 py-1 text-right">Exit $</th>
                    <th className="px-2 py-1 text-right">P&L</th>
                    <th className="px-2 py-1 text-right">Return</th>
                    <th className="px-2 py-1 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {data.trades.map((t, i) => (
                    <tr key={i} className={t.is_idle ? 'bg-blue-900/10' : t.is_open ? 'bg-yellow-900/10' : ''}>
                      <td className="px-2 py-1 text-gray-400">{t.entry_date?.slice(0, 10)}</td>
                      <td className="px-2 py-1 text-gray-400">{t.is_open ? '—' : t.exit_date?.slice(0, 10)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-300">{t.is_idle ? t.idle_asset : ''} ${Number(t.entry_price).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-300">${Number(t.exit_price).toFixed(2)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-medium ${valColor(t.pnl)}`}>${Number(t.pnl).toFixed(2)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums ${valColor(t.return_pct)}`}>{fmtPct(t.return_pct)}</td>
                      <td className="px-2 py-1 text-center">
                        {t.is_idle ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-medium">IDLE</span>
                        : t.is_open ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500 font-medium">OPEN</span>
                        : <span className="text-[9px] text-gray-500">trade</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* --- Scan Results Card — rendered inline in chat --- */
const ScanResultsCard = ({ data }) => {
  const [showAll, setShowAll] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(false);
  const rawResults = data.results || [];
  const summary = data.summary_stats || {};

  // Sort results by clicked column
  const sorted = React.useMemo(() => {
    if (!sortKey) return rawResults;
    return [...rawResults].sort((a, b) => {
      const av = a[sortKey] ?? (sortAsc ? Infinity : -Infinity);
      const bv = b[sortKey] ?? (sortAsc ? Infinity : -Infinity);
      return sortAsc ? av - bv : bv - av;
    });
  }, [rawResults, sortKey, sortAsc]);
  const display = showAll ? sorted : sorted.slice(0, 10);

  const handleSort = (key) => {
    if (sortKey === key) { setSortAsc(!sortAsc); }
    else { setSortKey(key); setSortAsc(key === 'max_drawdown'); } // drawdown ascending by default
  };
  const sortIcon = (key) => sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  return (
    <div className="max-w-full rounded-lg border border-green-300 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-green-200 dark:border-green-800 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
            Scan Complete — {rawResults.length} symbols ranked by {data.ranking_metric?.replace('_', ' ')}
          </h4>
          <div className="text-xs text-gray-500 mt-0.5">
            {data.strategy_name} · {data.start_date || 'max'} → {data.end_date || 'today'}
          </div>
        </div>
      </div>

      {/* Summary stats */}
      {Object.keys(summary).length > 0 && (
        <div className="px-4 py-2 border-b border-green-200 dark:border-green-800 flex gap-4 flex-wrap text-xs">
          {summary.avg_sharpe_ratio != null && <span>Avg Sharpe: <strong>{fmtNum(summary.avg_sharpe_ratio)}</strong></span>}
          {summary.avg_total_return != null && <span>Avg Return: <strong className={valColor(summary.avg_total_return)}>{fmtPct(summary.avg_total_return)}</strong></span>}
          {summary.avg_win_rate != null && <span>Avg Win Rate: <strong>{fmtPct(summary.avg_win_rate)}</strong></span>}
          {summary.best_total_return != null && <span>Best: <strong className="text-green-600">{fmtPct(summary.best_total_return)}</strong></span>}
        </div>
      )}

      {/* Results table — sortable columns */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 dark:bg-gray-800/50 text-[10px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Symbol</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('total_return')}>Return{sortIcon('total_return')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('buy_hold_return')}>B&H{sortIcon('buy_hold_return')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('sharpe_ratio')}>Sharpe{sortIcon('sharpe_ratio')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('max_drawdown')}>Max DD{sortIcon('max_drawdown')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('win_rate')}>Win Rate{sortIcon('win_rate')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('num_trades')}>Trades{sortIcon('num_trades')}</th>
              <th className="px-2 py-2 text-right cursor-pointer hover:text-gray-300 select-none" onClick={() => handleSort('profit_factor')}>P/F{sortIcon('profit_factor')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {display.map((r, idx) => (
              <tr key={r.symbol} className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 ${idx < 3 && !sortKey ? 'bg-brand-gold/5' : ''}`}>
                <td className="px-2 py-1.5 text-gray-500 font-medium">{sortKey ? idx + 1 : r.rank}</td>
                <td className="px-2 py-1.5 font-medium text-gray-800 dark:text-gray-200">
                  <div>{r.symbol}</div>
                  {r.sector && <div className="text-[9px] text-gray-400">{r.sector}</div>}
                </td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${valColor(r.total_return)}`}>{fmtPct(r.total_return)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${valColor(r.buy_hold_return)}`}>{fmtPct(r.buy_hold_return)}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums ${valColor(r.sharpe_ratio)}`}>{fmtNum(r.sharpe_ratio)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-red-500">{fmtPct(r.max_drawdown)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(r.win_rate)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">{r.num_trades}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmtNum(r.profit_factor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-green-200 dark:border-green-800 flex items-center gap-2 flex-wrap">
        {rawResults.length > 10 && (
          <button onClick={() => setShowAll(!showAll)} className="text-xs text-brand-gold hover:underline">
            {showAll ? 'Show top 10' : `Show all ${rawResults.length} results`}
          </button>
        )}
        {rawResults.length > 0 && (
          <a
            href={`/trading/backtest?symbol=${rawResults[0].symbol}&strategy=${data.strategy_name}`}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-auto"
          >
            Backtest #{1} ({rawResults[0].symbol}) in detail →
          </a>
        )}
      </div>
    </div>
  );
};


const PortfolioSimulationCard = ({ data }) => {
  const [showAllStocks, setShowAllStocks] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, contributors, weights

  if (!data || data.status === 'failed') {
    return (
      <div className="max-w-full rounded-lg border border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-4">
        <div className="text-sm text-red-600 dark:text-red-400">Portfolio simulation failed: {data?.error_message || 'Unknown error'}</div>
      </div>
    );
  }

  const m = data.portfolio_metrics || {};
  const eq = data.equity_curve || [];
  const topC = data.top_contributors || [];
  const bottomC = data.bottom_contributors || [];
  const perStock = data.per_stock || [];
  const weightMap = data.weights || {};

  const metrics = [
    { label: 'Portfolio CAGR', value: fmtPct(m.cagr), color: valColor(m.cagr) },
    { label: 'Sharpe', value: fmtNum(m.sharpe), color: valColor(m.sharpe) },
    { label: 'Max Drawdown', value: fmtPct(m.max_drawdown), color: 'text-red-400' },
    { label: `${data.idle_deployment || 'SPY'} CAGR`, value: fmtPct(m.spy_cagr), color: valColor(m.spy_cagr) },
    { label: 'Alpha', value: fmtPct(m.alpha), color: valColor(m.alpha) },
    { label: 'Annual Vol', value: fmtPct(m.annual_vol), color: 'text-gray-400' },
  ];

  const tabCls = (t) => `px-3 py-1 text-xs rounded-full transition-colors ${activeTab === t ? 'bg-brand-gold/20 text-brand-gold font-medium' : 'text-gray-500 hover:text-gray-300'}`;

  // Top 20 weights for chart
  const weightEntries = Object.entries(weightMap)
    .map(([sym, w]) => ({ sym, w: Number(w) }))
    .sort((a, b) => b.w - a.w)
    .slice(0, 20);

  return (
    <div className="max-w-full rounded-lg border border-purple-300 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-purple-200 dark:border-purple-800">
        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
          Portfolio Simulation — {data.strategy_name?.replace(/_/g, ' ')}
        </h4>
        <div className="text-xs text-gray-500 mt-0.5">
          {m.n_stocks_included || '?'} stocks · {data.weight_method?.replace(/_/g, ' ')} weight · {data.idle_deployment || 'SPY'} idle · {data.start_date || 'max'} → {data.end_date || 'today'}
          {m.n_stocks_excluded > 0 && <span className="text-yellow-500 ml-2">({m.n_stocks_excluded} excluded)</span>}
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-gray-200 dark:bg-gray-700 border-b border-purple-200 dark:border-purple-800">
        {metrics.map((mt, i) => (
          <div key={i} className="bg-white dark:bg-gray-900 px-3 py-2 text-center">
            <div className="text-[9px] uppercase tracking-wider text-gray-500">{mt.label}</div>
            <div className={`text-sm font-semibold tabular-nums ${mt.color}`}>{mt.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="px-4 py-2 flex gap-2 border-b border-purple-200 dark:border-purple-800">
        <button className={tabCls('overview')} onClick={() => setActiveTab('overview')}>Equity Curve</button>
        <button className={tabCls('contributors')} onClick={() => setActiveTab('contributors')}>Top / Bottom</button>
        <button className={tabCls('weights')} onClick={() => setActiveTab('weights')}>Weights</button>
        <button className={tabCls('monthly')} onClick={() => setActiveTab('monthly')}>Monthly Returns</button>
        <button className={tabCls('all')} onClick={() => setActiveTab('all')}>All Stocks</button>
      </div>

      {/* Tab content */}
      <div className="px-4 py-3">
        {activeTab === 'overview' && eq.length > 0 && (
          <PlotlyChart
            data={[
              {
                x: eq.map(p => p.date),
                y: eq.map(p => p.equity),
                type: 'scatter',
                mode: 'lines',
                line: { color: '#a855f7', width: 2 },
                fill: 'tozeroy',
                fillcolor: 'rgba(168, 85, 247, 0.08)',
                hovertemplate: '%{x|%b %d %Y}<br>Equity: %{y:.4f}<extra></extra>',
                name: 'Portfolio',
              },
              {
                x: eq.map(p => p.date),
                y: eq.map(p => p.drawdown),
                type: 'scatter',
                mode: 'lines',
                line: { color: '#ef4444', width: 1 },
                yaxis: 'y2',
                hovertemplate: '%{x|%b %d %Y}<br>DD: %{y:.1f}%<extra></extra>',
                name: 'Drawdown',
              },
            ]}
            layout={{
              height: 280,
              margin: { l: 50, r: 50, t: 10, b: 30 },
              yaxis: { title: 'Equity (1 = start)', tickformat: '.2f', gridcolor: '#374151', side: 'left' },
              yaxis2: { title: 'Drawdown %', overlaying: 'y', side: 'right', tickformat: '.0f', ticksuffix: '%', gridcolor: 'transparent' },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
              showlegend: true,
              legend: { x: 0, y: 1.1, orientation: 'h', font: { size: 10 } },
            }}
            style={{ minHeight: 280 }}
          />
        )}

        {activeTab === 'contributors' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top contributors */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-green-500 mb-2 font-medium">Top Contributors</div>
              <table className="w-full text-xs">
                <thead className="text-[9px] uppercase text-gray-500">
                  <tr><th className="text-left pb-1">Symbol</th><th className="text-right pb-1">Weight</th><th className="text-right pb-1">CAGR</th><th className="text-right pb-1">Contrib</th></tr>
                </thead>
                <tbody>
                  {topC.map(s => (
                    <tr key={s.symbol} className="border-t border-gray-800">
                      <td className="py-1 font-medium text-gray-200">{s.symbol}</td>
                      <td className="py-1 text-right tabular-nums text-gray-400">{fmtPct(s.weight)}</td>
                      <td className={`py-1 text-right tabular-nums ${valColor(s.cagr)}`}>{fmtPct(s.cagr)}</td>
                      <td className={`py-1 text-right tabular-nums font-medium ${valColor(s.contribution)}`}>{s.contribution != null ? s.contribution.toFixed(3) : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Bottom contributors */}
            {bottomC.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-red-500 mb-2 font-medium">Bottom Contributors</div>
                <table className="w-full text-xs">
                  <thead className="text-[9px] uppercase text-gray-500">
                    <tr><th className="text-left pb-1">Symbol</th><th className="text-right pb-1">Weight</th><th className="text-right pb-1">CAGR</th><th className="text-right pb-1">Contrib</th></tr>
                  </thead>
                  <tbody>
                    {bottomC.map(s => (
                      <tr key={s.symbol} className="border-t border-gray-800">
                        <td className="py-1 font-medium text-gray-200">{s.symbol}</td>
                        <td className="py-1 text-right tabular-nums text-gray-400">{fmtPct(s.weight)}</td>
                        <td className={`py-1 text-right tabular-nums ${valColor(s.cagr)}`}>{fmtPct(s.cagr)}</td>
                        <td className={`py-1 text-right tabular-nums font-medium ${valColor(s.contribution)}`}>{s.contribution != null ? s.contribution.toFixed(3) : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'weights' && weightEntries.length > 0 && (
          <PlotlyChart
            data={[{
              x: weightEntries.map(e => e.sym),
              y: weightEntries.map(e => e.w),
              type: 'bar',
              marker: { color: '#a855f7' },
              hovertemplate: '%{x}: %{y:.2f}%<extra></extra>',
            }]}
            layout={{
              height: 240,
              margin: { l: 40, r: 10, t: 10, b: 60 },
              xaxis: { tickangle: -45, tickfont: { size: 9 } },
              yaxis: { title: 'Weight %', ticksuffix: '%' },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#9ca3af', size: 10 },
            }}
            style={{ minHeight: 240 }}
          />
        )}

        {activeTab === 'monthly' && (() => {
          const monthlyData = (data.summary_stats?.monthly_returns || []);
          const yearlyData = (data.summary_stats?.yearly_returns || []);
          if (!monthlyData.length) return <div className="text-xs text-gray-500">No monthly data available</div>;
          const years = [...new Set(monthlyData.map(d => d.year))].sort();
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          // Build lookup: {year: {month: return}}
          const lookup = {};
          for (const d of monthlyData) {
            if (!lookup[d.year]) lookup[d.year] = {};
            lookup[d.year][d.month] = d.return;
          }
          const yearLookup = {};
          for (const d of yearlyData) yearLookup[d.year] = d.return;
          const cellColor = (v) => {
            if (v == null) return 'bg-gray-800/30 text-gray-600';
            if (v > 5) return 'bg-green-600/40 text-green-300';
            if (v > 2) return 'bg-green-600/25 text-green-400';
            if (v > 0) return 'bg-green-600/10 text-green-400';
            if (v > -2) return 'bg-red-600/10 text-red-400';
            if (v > -5) return 'bg-red-600/25 text-red-400';
            return 'bg-red-600/40 text-red-300';
          };
          return (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-xs">
                <thead className="text-[9px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Year</th>
                    {months.map(m => <th key={m} className="px-1.5 py-1.5 text-center w-12">{m}</th>)}
                    <th className="px-2 py-1.5 text-center font-bold">Year</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map(yr => (
                    <tr key={yr} className="border-t border-gray-800">
                      <td className="px-2 py-1 font-medium text-gray-300">{yr}</td>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(mo => {
                        const v = lookup[yr]?.[mo];
                        return (
                          <td key={mo} className={`px-1.5 py-1 text-center tabular-nums text-[11px] ${cellColor(v)}`}>
                            {v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}` : ''}
                          </td>
                        );
                      })}
                      <td className={`px-2 py-1 text-center tabular-nums font-semibold text-[11px] ${cellColor(yearLookup[yr])}`}>
                        {yearLookup[yr] != null ? `${yearLookup[yr] > 0 ? '+' : ''}${yearLookup[yr].toFixed(1)}%` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {activeTab === 'all' && (
          <div className="overflow-x-auto scrollbar-thin max-h-80">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 dark:bg-gray-800/50 text-[9px] uppercase tracking-wider text-gray-500 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">Symbol</th>
                  <th className="px-2 py-1.5 text-right">Weight</th>
                  <th className="px-2 py-1.5 text-right">CAGR</th>
                  <th className="px-2 py-1.5 text-right">Alpha</th>
                  <th className="px-2 py-1.5 text-right">Sharpe</th>
                  <th className="px-2 py-1.5 text-right">Max DD</th>
                  <th className="px-2 py-1.5 text-right">Trades</th>
                  <th className="px-2 py-1.5 text-right">Contrib</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {(showAllStocks ? perStock : perStock.slice(0, 20)).map(s => (
                  <tr key={s.symbol} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-2 py-1 font-medium text-gray-200">
                      <div>{s.symbol}</div>
                      {s.sector && <div className="text-[8px] text-gray-500">{s.sector}</div>}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-gray-400">{fmtPct(s.weight)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${valColor(s.cagr)}`}>{fmtPct(s.cagr)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${valColor(s.alpha)}`}>{fmtPct(s.alpha)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${valColor(s.sharpe)}`}>{fmtNum(s.sharpe)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-red-400">{fmtPct(s.max_dd)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-gray-500">{s.num_trades ?? 'N/A'}</td>
                    <td className={`px-2 py-1 text-right tabular-nums font-medium ${valColor(s.contribution)}`}>{s.contribution != null ? s.contribution.toFixed(3) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {perStock.length > 20 && (
              <div className="py-2 text-center">
                <button onClick={() => setShowAllStocks(!showAllStocks)} className="text-xs text-brand-gold hover:underline">
                  {showAllStocks ? 'Show top 20' : `Show all ${perStock.length} stocks`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer summary */}
      <div className="px-4 py-2 border-t border-purple-200 dark:border-purple-800 text-xs text-gray-500">
        Total return: <span className={valColor(m.total_return)}>{fmtPct(m.total_return)}</span>
        {m.spy_sharpe != null && <span className="ml-3">{data.idle_deployment || 'SPY'} Sharpe: {fmtNum(m.spy_sharpe)}</span>}
        {m.spy_max_dd != null && <span className="ml-3">{data.idle_deployment || 'SPY'} Max DD: {fmtPct(m.spy_max_dd)}</span>}
      </div>
    </div>
  );
};


const LegacyStrategyBuilder = () => {
  const { user } = useAuth();
  const [provider, setProvider] = useState('openai');
  const [useAgentV2, setUseAgentV2] = useState(true);
  const [modelName, setModelName] = useState('');
  const [providerModels, setProviderModels] = useState({});
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Extracted strategy from latest response
  const [extractedCode, setExtractedCode] = useState(null);
  const [extractedVectorizedCode, setExtractedVectorizedCode] = useState(null);
  const [extractedName, setExtractedName] = useState('');
  const [extractedClassName, setExtractedClassName] = useState('');
  const [extractedParams, setExtractedParams] = useState(null);
  const [extractedRanges, setExtractedRanges] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codeExpanded, setCodeExpanded] = useState(false);

  // Session ID for logging
  const [sessionId, setSessionId] = useState(() => `s-${crypto.randomUUID()}`);

  // Scan tracking
  const [activeScanId, setActiveScanId] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const scanPollRef = useRef(null);

  // Portfolio simulation tracking
  const [activeSimId, setActiveSimId] = useState(null);
  const [simResult, setSimResult] = useState(null);

  // Optimization polling
  const [activeOptId, setActiveOptId] = useState(null);
  const [optResult, setOptResult] = useState(null);

  // Saved strategies
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);

  const chatEndRef = useRef(null);

  useEffect(() => {
    tradingService.getBuilderModels()
      .then(res => {
        setProviderModels(res.data || {});
        // Set default model for current provider
        const pm = res.data || {};
        if (pm[provider]?.default) setModelName(pm[provider].default);
      })
      .catch(() => {});
    tradingService.getUserStrategies()
      .then(res => setSavedStrategies(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingSaved(false));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, scanResult, simResult]);

  // Poll for scan results — exponential backoff (3s → 5s → 8s → 12s, max 15s)
  useEffect(() => {
    if (!activeScanId) return;
    let delay = 3000;
    let timeoutId = null;
    const poll = async () => {
      try {
        const res = await tradingService.getScanResult(activeScanId);
        setScanResult(res.data);
        if (res.data.status === 'complete' || res.data.status === 'failed') {
          setActiveScanId(null);
          if (res.data.status === 'complete') {
            setMessages(prev => [...prev, { role: 'scan_result', data: res.data }]);
          }
          return; // stop polling
        }
      } catch { /* ignore */ }
      // Schedule next poll with increasing delay
      delay = Math.min(delay * 1.4, 15000);
      timeoutId = setTimeout(poll, delay);
    };
    poll(); // immediate first check
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [activeScanId]);

  // Poll for portfolio simulation results — same pattern as scan
  useEffect(() => {
    if (!activeSimId) return;
    let delay = 3000;
    let timeoutId = null;
    const poll = async () => {
      try {
        const res = await tradingService.getPortfolioSimulation(activeSimId);
        setSimResult(res.data);
        if (res.data.status === 'complete' || res.data.status === 'failed') {
          setActiveSimId(null);
          if (res.data.status === 'complete') {
            setMessages(prev => [...prev, { role: 'portfolio_sim_result', data: res.data }]);
          } else {
            setMessages(prev => [...prev, { role: 'error', text: `Portfolio simulation failed: ${res.data.error_message || 'unknown error'}` }]);
          }
          return;
        }
      } catch { /* ignore */ }
      delay = Math.min(delay * 1.4, 15000);
      timeoutId = setTimeout(poll, delay);
    };
    poll();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [activeSimId]);

  // Poll for optimization results
  useEffect(() => {
    if (!activeOptId) return;
    let delay = 3000;
    let timeoutId = null;
    const poll = async () => {
      try {
        const res = await tradingService.getOptimizationStatus(activeOptId);
        setOptResult(res.data);
        if (res.data.status === 'complete' || res.data.status === 'failed') {
          setActiveOptId(null);
          if (res.data.status === 'complete' && res.data.best_result) {
            setMessages(prev => [...prev, {
              role: 'optimize_result',
              data: {
                success: true,
                symbol: res.data.symbol,
                combinations_tested: res.data.total_combos,
                maximize: res.data.maximize,
                best: res.data.best_result,
                top_10: (res.data.results || []).slice(0, 10),
              },
            }]);
          } else if (res.data.status === 'failed') {
            setMessages(prev => [...prev, { role: 'error', text: `Optimization failed: ${res.data.error_message || 'unknown'}` }]);
          }
          return;
        }
      } catch { /* ignore */ }
      delay = Math.min(delay * 1.4, 15000);
      timeoutId = setTimeout(poll, delay);
    };
    poll();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, [activeOptId]);

  // Handler for OptimizationConfigCard "Run" button
  const handleRunOptimization = (optId, totalCombos) => {
    setActiveOptId(optId);
    setOptResult({ status: 'pending', total_combos: totalCombos, completed_combos: 0 });
    setMessages(prev => [...prev, { role: 'opt_started', total: totalCombos }]);
  };

  // Direct-action interceptor: run real APIs for backtest/scan commands
  // instead of going through the LLM (which hallucates "running..." text)
  const handleDirectAction = useCallback(async (msg) => {
    const lower = msg.toLowerCase();

    // === SAVE: any message about saving/activating the strategy ===
    if (/save|activate/i.test(lower) && !/don.t save|not save/i.test(lower)) {
      if (!extractedCode || !extractedClassName) {
        setMessages(prev => [...prev, { role: 'user', text: msg }, { role: 'error', text: 'No strategy code to save. Generate a strategy first.' }]);
        return true;
      }
      setMessages(prev => [...prev, { role: 'user', text: msg }]);
      setSending(true);
      try {
        const saveRes = await tradingService.strategyBuilderSave({
          name: extractedName || extractedClassName.replace('Logic', ''),
          code: extractedCode,
          class_name: extractedClassName,
          parameters: extractedParams || {},
          parameter_ranges: extractedRanges || {},
          vectorized_code: extractedVectorizedCode || undefined,
        });
        const saved = saveRes.data;
        setSavedStrategies(prev => [saved, ...prev.filter(s => s.id !== saved.id)]);
        // Show save + test results
        const t = saved.test;
        if (t && t.success && t.signals > 0) {
          setMessages(prev => [...prev, { role: 'system', text: `Strategy "${saved.name}" saved (custom:${saved.id}). Tested: ${t.signals} signals (${t.buys} buys, ${t.sells} sells) on ${t.bars_tested} bars.` }]);
        } else if (t && t.success && t.signals === 0) {
          setMessages(prev => [...prev,
            { role: 'system', text: `Strategy "${saved.name}" saved (custom:${saved.id}).` },
            { role: 'error', text: `Warning: 0 signals on ${t.bars_tested} bars. Conditions may be too strict. Try "Ask AI to simplify" or adjust parameters.` },
          ]);
        } else if (t && !t.success) {
          setMessages(prev => [...prev,
            { role: 'system', text: `Strategy "${saved.name}" saved (custom:${saved.id}).` },
            { role: 'error', text: `Test failed: ${t.error}. You can still backtest but results may be unexpected.` },
          ]);
        } else {
          setMessages(prev => [...prev, { role: 'system', text: `Strategy "${saved.name}" saved (custom:${saved.id}).` }]);
        }
      } catch (e) {
        const errMsg = e.response?.data?.error || e.message;
        setMessages(prev => [...prev, { role: 'error', text: `Save failed: ${errMsg}` }]);
        // Show "Ask AI to fix" button
        setMessages(prev => [...prev, {
          role: 'assistant',
          sections: [{ type: 'text', content: 'The strategy code has an issue. Would you like me to fix it?' }],
          actions: [
            { label: 'Ask AI to fix this', value: `Fix this error in the strategy code: ${errMsg}. Return the corrected code.`, primary: true },
            { label: 'Save anyway', value: 'force_save' },
          ],
        }]);
      } finally {
        setSending(false);
      }
      return true;
    }

    // === BACKTEST: REQUIRES a saved strategy ===
    const btMatch = msg.match(/(?:backtest|test|run|try)[\s_]+(?:on[\s_]+|this[\s_]+on[\s_]+|it[\s_]+on[\s_]+)?(\w{1,5})/i);
    if (btMatch && /backtest|test.*on|run.*on|try.*on/i.test(lower)) {
      if (savedStrategies.length === 0) {
        setMessages(prev => [...prev, { role: 'user', text: msg }, { role: 'error', text: 'Save the strategy first (click "Save & Activate"), then backtest.' }]);
        return true;
      }
      const symbol = btMatch[1].toUpperCase();
      const strategyRef = `custom:${savedStrategies[0].id}`;
      setMessages(prev => [...prev, { role: 'user', text: msg }]);
      setSending(true);

      const paramsUsed = savedStrategies[0]?.parameters || extractedParams || {};
      const paramStr = Object.entries(paramsUsed).map(([k, v]) => `${k}=${v}`).join(', ');
      setMessages(prev => [...prev, { role: 'system', text: `Running backtest on ${symbol} with custom:${savedStrategies[0].id} (${paramStr || 'defaults'})` }]);
      try {
        const res = await tradingService.runBacktest({
          strategy: strategyRef,
          symbol,
          parameters: extractedParams || {},
          start_date: '2020-01-01',
          source: 'consolidated',
          mode: 'single',
        });
        if (res.data?.success) {
          setMessages(prev => [...prev, { role: 'backtest_result', data: res.data }]);
        } else {
          setMessages(prev => [...prev, { role: 'error', text: res.data?.error || 'Backtest failed' }]);
        }
      } catch (e) {
        setMessages(prev => [...prev, { role: 'error', text: e.response?.data?.error || e.message }]);
      } finally {
        setSending(false);
      }
      return true; // handled
    }
    return false; // not a direct action
  }, [savedStrategies, extractedCode, extractedVectorizedCode, extractedClassName, extractedName, extractedParams, extractedRanges]);

  const sendMessage = useCallback(async (overrideMsg) => {
    const userMsg = (overrideMsg || input).trim();
    if (!userMsg || sending) return;

    // Try direct action first (bypass LLM for backtest/scan commands)
    setInput('');
    // v1 interceptors only — skip entirely in v2 mode
    if (!useAgentV2) {
      const handled = await handleDirectAction(userMsg);
      if (handled) return;
    }

    setError(null);
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setSending(true);

    try {
      if (useAgentV2) {
        // --- Agent v2 (LangGraph) ---
        const res = await tradingService.agentChat({
          message: userMsg, session_id: sessionId,
          provider, model: modelName || undefined,
        });
        const data = res.data;
        // Show agent's text response with contextual actions
        if (data.response) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            sections: [{ type: 'text', content: data.response }],
            actions: data.actions || [],
          }]);
        }
        // Show backtest results from tool calls
        for (const bt of (data.backtest_results || [])) {
          if (bt.success) setMessages(prev => [...prev, { role: 'backtest_result', data: bt }]);
        }
        // Show scan results
        for (const sc of (data.scan_results || [])) {
          if (sc.scan_id) {
            setActiveScanId(sc.scan_id);
            setScanResult({ status: 'pending', total_symbols: sc.total_symbols || 500, completed_symbols: 0 });
            setMessages(prev => [...prev, { role: 'scan_started', total: sc.total_symbols || 500 }]);
          }
        }
        // Show optimize results
        for (const opt of (data.optimize_results || [])) {
          if (opt.success) setMessages(prev => [...prev, { role: 'optimize_result', data: opt }]);
        }
        // Show data query results
        for (const qr of (data.query_results || [])) {
          if (qr.data) setMessages(prev => [...prev, { role: 'query_result', data: qr }]);
        }
        // Show strategy build results
        for (const sr of (data.strategy_results || [])) {
          if (sr.success) setMessages(prev => [...prev, { role: 'strategy_result', data: sr }]);
        }
        // Show portfolio simulation (async — start polling)
        for (const ps of (data.portfolio_sim_results || [])) {
          if (ps.simulation_id) {
            setActiveSimId(ps.simulation_id);
            setSimResult({ status: 'pending', total_symbols: ps.total_symbols || 500, completed_symbols: 0 });
            setMessages(prev => [...prev, { role: 'sim_started', total: ps.total_symbols || 500, weight_method: ps.weight_method, idle: ps.idle_deployment }]);
          }
        }
        // Show optimization config cards
        for (const cfg of (data.optimization_configs || [])) {
          setMessages(prev => [...prev, { role: 'optimization_config', data: cfg }]);
        }
      } else {
        // --- Agent v1 (legacy) ---
      const res = await tradingService.strategyBuilderChat({
        provider,
        model: modelName || undefined,
        message: userMsg,
        chat_history: chatHistory,
        api_key: apiKey || undefined,
        session_id: sessionId,
      });
      if (res.data.session_id) setSessionId(res.data.session_id);

      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        sections: data.sections || [],
        actions: data.actions || [],
      }]);
      setChatHistory(data.messages);

      if (data.code) {
        setExtractedCode(data.code);
        setExtractedVectorizedCode(data.vectorized_code || null);
        setExtractedClassName(data.class_name || '');
        setExtractedParams(data.parameters);
        setExtractedRanges(data.parameter_ranges);
        if (!extractedName && data.class_name) {
          // Split camelCase but keep consecutive capitals together (e.g. NVDASector → NVDA Sector)
          setExtractedName(
            data.class_name
              .replace('Logic', '')
              .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .trim()
          );
        }
      }

      // Generic action dispatch — handles ANY action type the LLM returns
      // Adding a new action = one entry in this registry
      const ACTION_DISPATCH = {
        backtest: async (action) => {
          setMessages(prev => [...prev, { role: 'system', text: `Running ${action.strategy || 'strategy'} on ${action.symbol}...` }]);
          const res = await tradingService.runBacktest({
            strategy: action.strategy || 'ema_crossover', symbol: action.symbol,
            parameters: action.parameters || {}, start_date: action.start_date || '2020-01-01',
            source: 'consolidated', mode: 'single',
          });
          return { role: 'backtest_result', data: res.data };
        },
        scan: async (action) => {
          setMessages(prev => [...prev, { role: 'system', text: 'Starting scan...' }]);
          const res = await tradingService.startScan(action);
          if (res.data?.scan_id) {
            setActiveScanId(res.data.scan_id);
            setScanResult({ status: 'pending', total_symbols: res.data.total_symbols || 500, completed_symbols: 0 });
            return { role: 'scan_started', total: res.data.total_symbols || 500 };
          }
          return null;
        },
        optimize: async (action) => {
          setMessages(prev => [...prev, { role: 'system', text: `Optimizing ${action.strategy} on ${action.symbol}...` }]);
          const res = await tradingService.runBacktest({ ...action, mode: 'optimize' });
          return { role: 'optimize_result', data: res.data };
        },
        compare: async (action) => {
          setMessages(prev => [...prev, { role: 'system', text: `Comparing strategies on ${action.symbol}...` }]);
          // Run each strategy as a separate backtest
          const results = [];
          for (const strat of (action.strategies || [])) {
            try {
              const res = await tradingService.runBacktest({
                strategy: strat.name, symbol: action.symbol,
                parameters: strat.parameters || {}, start_date: action.start_date || '2020-01-01',
                source: 'consolidated', mode: 'single',
              });
              results.push({ ...res.data, strategy_label: strat.label || strat.name });
            } catch { results.push({ success: false, strategy_label: strat.label || strat.name }); }
          }
          return { role: 'compare_result', data: { success: true, comparisons: results, symbol: action.symbol } };
        },
        price_chart: async (action) => {
          const res = await tradingService.getConsolidatedPrices(action.symbol_id || 1, action.days || 365);
          return { role: 'price_chart_result', data: res.data };
        },
        data_check: async (action) => {
          const res = await tradingService.backtestPreflight(action);
          return { role: 'data_check_result', data: res.data };
        },
      };

      for (const [actionType, handler] of Object.entries(ACTION_DISPATCH)) {
        const actionData = data[`${actionType}_action`] || data.structured?.[actionType];
        if (!actionData) continue;
        try {
          const result = await handler(actionData);
          if (result) setMessages(prev => [...prev, result]);
        } catch (e) {
          setMessages(prev => [...prev, { role: 'error', text: `${actionType} failed: ${e.response?.data?.error || e.message}` }]);
        }
        break; // Only execute one action per response
      }
      } // end v1 else
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message;
      setError(errMsg);
      setMessages(prev => [...prev, { role: 'error', text: errMsg }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, provider, apiKey, chatHistory, extractedName]);

  const handleSave = async () => {
    if (!extractedCode || !extractedClassName) return;
    setSaving(true);
    setError(null);

    try {
      const res = await tradingService.strategyBuilderSave({
        name: extractedName || extractedClassName,
        code: extractedCode,
        class_name: extractedClassName,
        description: messages.find(m => m.role === 'user')?.text || '',
        parameters: extractedParams || {},
        parameter_ranges: extractedRanges || {},
        chat_history: chatHistory,
        vectorized_code: extractedVectorizedCode || undefined,
      });
      setSavedStrategies(prev => [res.data, ...prev.filter(s => s.id !== res.data.id)]);
      setError(null);
      // Add success message to chat
      setMessages(prev => [...prev, { role: 'system', text: `Strategy "${res.data.name}" saved and ready for backtesting.` }]);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this strategy?')) return;
    try {
      await tradingService.deleteUserStrategy(id);
      setSavedStrategies(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setChatHistory([]);
    setExtractedCode(null);
    setExtractedVectorizedCode(null);
    setExtractedClassName('');
    setExtractedName('');
    setExtractedParams(null);
    setExtractedRanges(null);
    setError(null);
    setSessionId(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  };

  const cardCls = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm dark:shadow-none';

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4">
      {/* Left: Chat panel */}
      <div className="lg:col-span-2 flex flex-col" style={{ minHeight: '70vh' }}>
        {/* Provider + key config */}
        <div className={`${cardCls} p-3 mb-3`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Provider:</span>
            {PROVIDERS.map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setProvider(p.id);
                  const pm = providerModels[p.id];
                  setModelName(pm?.default || '');
                }}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  provider === p.id
                    ? 'bg-brand-gold text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
            {providerModels[provider]?.models?.length > 1 && (
              <select
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-gray-700 dark:text-gray-300 outline-none"
              >
                {providerModels[provider].models.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
            <span className="w-px h-5 bg-gray-300 dark:bg-gray-700" />
            {user?.is_admin ? (
              <span className="text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">Platform key</span>
            ) : (
              <div className="flex items-center gap-1.5">
                <MdKey size={14} className="text-gray-400" />
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder="Your API key"
                  className="bg-transparent border-b border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 outline-none w-48 py-0.5"
                />
                <button onClick={() => setShowKey(!showKey)} className="text-[10px] text-gray-400">{showKey ? 'hide' : 'show'}</button>
              </div>
            )}
            <label className="flex items-center gap-1.5 ml-2 cursor-pointer">
              <span className={`text-[10px] ${useAgentV2 ? 'text-brand-gold font-medium' : 'text-gray-500'}`}>v2</span>
              <div className="relative">
                <input type="checkbox" checked={useAgentV2} onChange={(e) => setUseAgentV2(e.target.checked)} className="sr-only" />
                <div className={`w-7 h-4 rounded-full transition-colors ${useAgentV2 ? 'bg-brand-gold' : 'bg-gray-600'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white transition-transform mt-0.5 ${useAgentV2 ? 'translate-x-3.5 ml-0' : 'translate-x-0.5'}`} />
                </div>
              </div>
            </label>
            {messages.length > 0 && (
              <button onClick={resetChat} className="ml-auto text-xs text-red-500 hover:text-red-700">New Chat</button>
            )}
          </div>
        </div>

        {/* Chat messages */}
        <div className={`${cardCls} flex-1 flex flex-col overflow-hidden`}>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="py-8 text-center">
                <MdSmartToy size={40} className="mx-auto mb-3 text-brand-gold opacity-60" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">Agentic Trader</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  Tell me what you want to trade. I'll search our strategy library, help you configure parameters, and run backtests — all right here.
                </p>
                <div className="flex flex-col gap-2 max-w-lg mx-auto">
                  {[
                    'I want a momentum strategy for NVDA',
                    'Find me a mean reversion setup for AAPL since 2022',
                    'Build something that trades on volume spikes and RSI',
                    'Compare different strategies on TSLA',
                  ].map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(prompt)}
                      className="text-left px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800/20 hover:border-brand-gold/50 hover:bg-brand-gold/5 hover:shadow-sm transition-all text-sm text-gray-700 dark:text-gray-300 hover:text-brand-gold"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-brand-gold/10 text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {msg.text}
                  </div>
                ) : msg.role === 'error' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                    {msg.text}
                  </div>
                ) : msg.role === 'system' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 flex items-center gap-2">
                    <MdCheck size={16} /> {msg.text}
                  </div>
                ) : msg.role === 'scan_started' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                    Scan started — testing strategy across {msg.total} symbols...
                  </div>
                ) : msg.role === 'backtest_result' ? (
                  <BacktestResultCard data={msg.data} />
                ) : msg.role === 'optimize_result' ? (
                  <OptimizeResultCard data={msg.data} />
                ) : msg.role === 'query_result' ? (
                  <DataQueryCard data={msg.data} />
                ) : msg.role === 'strategy_result' ? (
                  <StrategyPreviewCard data={msg.data} onAction={(text) => sendMessage(text)} />
                ) : msg.role === 'compare_result' ? (
                  <CompareResultCard data={msg.data} />
                ) : msg.role === 'price_chart_result' ? (
                  <PriceChartCard data={msg.data} />
                ) : msg.role === 'data_check_result' ? (
                  <DataCheckCard data={msg.data} />
                ) : msg.role === 'scan_result' ? (
                  <ScanResultsCard data={msg.data} />
                ) : msg.role === 'sim_started' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
                    Portfolio simulation started — running strategy across {msg.total} symbols ({msg.weight_method?.replace(/_/g, ' ')} weight, {msg.idle} idle)...
                  </div>
                ) : msg.role === 'portfolio_sim_result' ? (
                  <PortfolioSimulationCard data={msg.data} />
                ) : msg.role === 'optimization_config' ? (
                  <OptimizationConfigCard data={msg.data} onRunOptimization={handleRunOptimization} />
                ) : msg.role === 'opt_started' ? (
                  <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400">
                    Optimization started — testing {msg.total?.toLocaleString()} parameter combinations...
                  </div>
                ) : msg.role === 'assistant' ? (
                  <StructuredMessage
                    sections={msg.sections}
                    actions={msg.actions}
                    onQuickReply={(text) => sendMessage(text)}
                  />
                ) : null}
              </div>
            ))}
            {/* Scan progress indicator */}
            {activeScanId && scanResult && scanResult.status !== 'complete' && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Scanning symbols...</span>
                  </div>
                  <div className="w-48 bg-blue-200 dark:bg-blue-800 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((scanResult.completed_symbols || 0) / Math.max(scanResult.total_symbols || 1, 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-blue-500 mt-1 block">{scanResult.completed_symbols || 0} / {scanResult.total_symbols || '?'} symbols</span>
                </div>
              </div>
            )}
            {/* Portfolio simulation progress indicator */}
            {activeSimId && simResult && simResult.status !== 'complete' && simResult.status !== 'failed' && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-300 border-t-purple-600" />
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-400">Running portfolio simulation...</span>
                  </div>
                  <div className="w-48 bg-purple-200 dark:bg-purple-800 rounded-full h-1.5">
                    <div
                      className="bg-purple-600 h-1.5 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((simResult.completed_symbols || 0) / Math.max(simResult.total_symbols || 1, 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-purple-500 mt-1 block">{simResult.completed_symbols || 0} / {simResult.total_symbols || '?'} backtests</span>
                </div>
              </div>
            )}
            {/* Optimization progress indicator */}
            {activeOptId && optResult && optResult.status !== 'complete' && optResult.status !== 'failed' && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  {optResult.status === 'pending' && (optResult.queue_position || 0) > 0 ? (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-3 w-3 rounded-full bg-yellow-500" />
                        <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">In queue</span>
                      </div>
                      <div className="text-xs text-yellow-700 dark:text-yellow-300 leading-relaxed">
                        {optResult.queue_position} optimization{optResult.queue_position === 1 ? '' : 's'} ahead of yours.
                        {optResult.eta_minutes ? <> Estimated start: <strong>~{optResult.eta_minutes} min</strong>.</> : null}
                      </div>
                      <div className="text-[11px] text-yellow-600/80 dark:text-yellow-500/80 mt-1.5 italic">
                        You can close this tab — your run will continue in the background and results will be saved when ready.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-yellow-300 border-t-yellow-600" />
                        <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">Optimizing...</span>
                      </div>
                      <div className="w-48 bg-yellow-200 dark:bg-yellow-800 rounded-full h-1.5">
                        <div
                          className="bg-yellow-600 h-1.5 rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((optResult.completed_combos || 0) / Math.max(optResult.total_combos || 1, 1)) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-yellow-500 mt-1 block">{optResult.completed_combos || 0} / {optResult.total_combos || '?'} combinations</span>
                    </>
                  )}
                </div>
              </div>
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-gray-800 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Describe your strategy or refine parameters..."
                className={`${inputCls} flex-1`}
                disabled={sending}
              />
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); sendMessage(); }}
                disabled={sending || !input.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-gold text-white rounded-lg text-sm disabled:opacity-40 hover:opacity-90 touch-manipulation"
                style={{ minWidth: 44, minHeight: 44 }}
              >
                <MdSend size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Code preview + saved strategies */}
      <div className="space-y-4">
        {/* Extracted code panel */}
        {extractedCode && (
          <div className={`${cardCls} overflow-hidden`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <MdCode size={16} className="text-brand-gold" />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Generated Strategy</span>
              </div>
              <button onClick={() => setCodeExpanded(!codeExpanded)} className="text-gray-400">
                {codeExpanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
              </button>
            </div>

            <div className="px-4 py-3 space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">Strategy Name</label>
                <input
                  type="text"
                  value={extractedName}
                  onChange={e => setExtractedName(e.target.value)}
                  className={`${inputCls} text-sm`}
                  placeholder="My RSI Strategy"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Class: <code className="text-brand-gold">{extractedClassName}</code></span>
              </div>
              {extractedParams && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">Default Parameters</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(extractedParams).map(([k, v]) => (
                      <span key={k} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300">
                        {k}={String(v)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {codeExpanded && (
              <div className="px-4 pb-3">
                <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-72 scrollbar-thin">
                  {extractedCode}
                </pre>
              </div>
            )}

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving || !extractedClassName}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-brand-gold text-white rounded-lg text-sm disabled:opacity-40 hover:opacity-90"
              >
                <MdSave size={16} /> {saving ? 'Saving...' : 'Save & Activate'}
              </button>
            </div>
            {error && <div className="px-4 pb-3 text-xs text-red-500">{error}</div>}
          </div>
        )}

        {/* Saved custom strategies */}
        <div className={`${cardCls} p-4`}>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Custom Strategies <span className="text-gray-400 font-normal">({savedStrategies.length})</span>
          </h3>
          {loadingSaved && <div className="text-xs text-gray-400">Loading...</div>}
          {!loadingSaved && savedStrategies.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-4 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              No custom strategies yet. Chat to create one.
            </div>
          )}
          <div className="space-y-2">
            {savedStrategies.map(s => (
              <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-100 dark:bg-gray-800/30 rounded-md">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{s.name}</div>
                  <div className="text-[10px] text-gray-400">{s.class_name} · by {s.author}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    s.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500'
                  }`}>{s.status}</span>
                  <button onClick={() => handleDelete(s.id)} className="p-1 text-gray-400 hover:text-red-500"><MdDelete size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// The old Agentic Trader is switched off while the new strategy agent is built (2026-10-21). The server says so on
// strategy-builder/models/ ({disabled, message}); the old chat renders only if it is switched back on.
const StrategyBuilderPage = () => {
  const [state, setState] = useState({ loading: true, disabled: false, message: '' });
  useEffect(() => {
    tradingService.getBuilderModels()
      .then(res => setState({ loading: false, disabled: !!res.data?.disabled, message: res.data?.message || '' }))
      .catch(() => setState({ loading: false, disabled: false, message: '' }));
  }, []);
  if (state.loading) return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
  if (state.disabled) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 text-center">
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100 mb-2">Agentic Trader</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">{state.message}</p>
      </div>
    );
  }
  return <LegacyStrategyBuilder />;
};

export default StrategyBuilderPage;
